import { useState, useEffect, useCallback } from 'react'
import { getApiBaseUrl } from '@/config/apiBase'
import type { PortalData } from '@/types/portalCliente'

const API_BASE = getApiBaseUrl()

export function usePortalCliente(token: string | undefined) {
  const [data, setData] = useState<PortalData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [npsNota, setNpsNota] = useState<number | null>(null)
  const [npsComentario, setNpsComentario] = useState('')
  const [npsEnviado, setNpsEnviado] = useState(false)
  const [npsSending, setNpsSending] = useState(false)

  const [showCheckinForm, setShowCheckinForm] = useState(false)
  const [checkinNota, setCheckinNota] = useState(5)
  const [checkinComentario, setCheckinComentario] = useState('')
  const [checkinEnviando, setCheckinEnviando] = useState(false)
  const [checkinEnviado, setCheckinEnviado] = useState(false)

  const fetchPortal = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/portal/${token}`)
      if (!res.ok) {
        if (res.status === 404) setError('Portal não encontrado')
        else setError('Erro ao carregar dados')
        return
      }
      const json = await res.json()
      setData(json.data)
    } catch {
      setError('Erro de conexão')
    } finally {
      setLoading(false)
    }
  }, [token])

  useEffect(() => {
    if (token) fetchPortal()
  }, [token, fetchPortal])

  const handleNpsSubmit = async () => {
    if (npsNota === null || !token) return
    setNpsSending(true)
    try {
      const res = await fetch(`${API_BASE}/portal/${token}/nps`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ nota: npsNota, comentario: npsComentario || undefined }),
      })
      if (res.ok) {
        setNpsEnviado(true)
        setNpsComentario('')
        fetchPortal()
      }
    } catch {
      // silent
    } finally {
      setNpsSending(false)
    }
  }

  const handleCheckinSubmit = async () => {
    if (!token) return
    setCheckinEnviando(true)
    try {
      const res = await fetch(`${API_BASE}/portal/${token}/checkin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          nota: checkinNota,
          comentario: checkinComentario || undefined,
        }),
      })
      if (res.ok) {
        setCheckinEnviado(true)
        setCheckinComentario('')
        fetchPortal()
      }
    } catch {
      // silent
    } finally {
      setCheckinEnviando(false)
    }
  }

  return {
    data,
    loading,
    error,
    npsNota,
    setNpsNota,
    npsComentario,
    setNpsComentario,
    npsEnviado,
    npsSending,
    handleNpsSubmit,
    showCheckinForm,
    setShowCheckinForm,
    checkinNota,
    setCheckinNota,
    checkinComentario,
    setCheckinComentario,
    checkinEnviando,
    checkinEnviado,
    handleCheckinSubmit,
  }
}
